import { AppNode, EdgeData } from "@features/builder/graph/types"
import { getEdgeLabel, hasEdgeCycle, isSourceMissingUbound } from "@features/builder/helpers/edges"
import useNodeLock from "@features/builder/hooks/useNodeLock"
import {
  Edge,
  Handle,
  HandleProps,
  InternalNode,
  ReactFlowInstance,
  useConnection,
  useReactFlow,
} from "@xyflow/react"
import css from "./EdgeHandle.module.css"
import cls from "classnames"
import { useMemo } from "react"

type EdgeHandleProps = Omit<HandleProps, "isConnectable"> & {
  node: AppNode
}

const EdgeHandle = (props: EdgeHandleProps) => {
  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  const connection = useConnection<AppNode>()
  const source = connection.fromNode
  const target = props.node

  const nodeLock = useNodeLock({ nodeId: props.node.id })
  const isTargetLocked = !!nodeLock

  const sourceLock = useNodeLock({ nodeId: connection.fromNode?.id ?? "" })
  const isSourceLocked = !!sourceLock

  const isConnectionInvalid =
    !useMemo(
      () => validateConnection({ source, target, reactFlowInstance }),
      [source, target, reactFlowInstance],
    ) && props.type === "source"

  const isNodeLocked =
    !connection.inProgress && (isTargetLocked || isSourceLocked) && props.type === "source"

  const isNodeLockedInProgress =
    connection.inProgress && (isTargetLocked || isSourceLocked) && props.type === "source"

  const isNodeInProgress = connection.inProgress && props.type === "target"

  // undefined is default value which means "true" - not sure why reactflow just do not pass "true"
  const { connectable = true } = props.node

  return (
    <Handle
      {...props}
      isConnectable={connectable}
      className={cls(
        isConnectionInvalid && css.invalidHandle, //
        isNodeLockedInProgress && css.invalidHandle,
        isNodeLocked && css.lockedHandle,
        isNodeInProgress && css.largeHitBox,
      )}
    />
  )
}

export default EdgeHandle

function validateConnection({
  source,
  target,
  reactFlowInstance,
}: {
  source: InternalNode<AppNode> | null
  target: AppNode
  reactFlowInstance: ReactFlowInstance<AppNode, Edge<EdgeData>>
}) {
  const errors: boolean[] = []
  if (source) {
    errors.push(!!getEdgeLabel({ source, target }))
    const sourceNode = reactFlowInstance.getNode(source.id)
    if (sourceNode) {
      errors.push(
        !!hasEdgeCycle({
          source: sourceNode,
          target,
          nodes: reactFlowInstance.getNodes(),
          edges: reactFlowInstance.getEdges(),
        }),
      )
      errors.push(isSourceMissingUbound({ source: sourceNode, target }))
    }
  }
  return !errors.some(Boolean)
}
